/* eslint-disable max-lines */

import { compact } from 'lodash';
import { removeFromArray } from '../utils';
import { getUserTariff } from '../../../../shared/utils';
import { selectIsByHomeowner, selectIsPublishedOffer } from '../../../../shared/selectors/offer';
import { selectConfigValue } from '../../../../shared/selectors/config';
import { selectCanPublishAsHomeowner, selectIsHomeowner } from '../../../../shared/selectors/user';

export default function AdSegmentsService(
  userService,
  addFormLayoutService,
  jkService,
  store,
  authService,
  addFormService,
  publishTermsService,
  commercialOwnerService,
  rentHelperService,
) {
  'ngInject';

  const hiddenSegments = [];

  const setSegmentVisible = (segment, value) => {
    const segmentIndex = hiddenSegments.indexOf(segment);
    if (segmentIndex !== -1 && value) {
      hiddenSegments.splice(segmentIndex, 1);
    }
    if (segmentIndex === -1 && !value) {
      hiddenSegments.push(segment);
    }
  };

  const isSegmentHidden = segment => hiddenSegments.includes(segment);

  const getSuburbanSegments = ({ objectType, rentDuration }) => {
    if (rentDuration) {
      return ['about-object'];
    }

    return [objectType !== 'land' && 'about-house', 'about-land', 'facilities'].filter(Boolean);
  };

  const getSegmentsByCategory = ({ objectType, rentDuration, coworkingOfferType, rosreestrData }) => {
    if (rentDuration === 'short') {
      // Посуточная аренда
      if (objectType === 'house') {
        return ['about-object', 'description-and-photos', 'price-and-deal-terms'];
      }

      return ['about-object', 'about-building', 'homeowner', 'description-and-photos', 'price-and-deal-terms'];
    } else if (objectType === 'newBuildingFlat') {
      // Новостройки
      const { isAgentCplModerationEnabled } = window.addFormState;
      const { user } = store.getState();
      const isCplNewbuilding = addFormService.isCplNewbuildingSelected();
      const isAuthUser = userService.isAuthenticated;
      const { isBuilder, isNonCommercial } = getUserTariff(user);

      const canShowGetAdditionalBenefits =
        isAgentCplModerationEnabled &&
        isCplNewbuilding &&
        (isNonCommercial || !isAuthUser || (isAuthUser && !isBuilder));
      const getAdditionalBenefitsSegment = canShowGetAdditionalBenefits ? 'get-additional-benefits' : null;

      return [
        'about-building',
        'about-object',
        'description-and-photos',
        getAdditionalBenefitsSegment,
        'price-and-deal-terms',
      ].filter(Boolean);
    } else if (['house', 'townhouse', 'cottage', 'houseShare', 'land'].includes(objectType)) {
      // Дом / дача / коттедж / доля в доме / участок
      const segments = getSuburbanSegments({ objectType, rentDuration });

      return [
        !rentDuration && 'cadastral-numbers-suburban',
        rosreestrData && 'rosreestr-data',
        ...segments,
        'description-and-photos',
        'price-and-deal-terms',
      ].filter(Boolean);
    }

    // TODO: нужно логику по формированию блоков коммерческой вынести в отдельный сервис
    // категории коммерческой без возможность податься как соб коммерческой
    if (objectType === 'coworking' && coworkingOfferType) {
      return ['about-object', 'amenities', 'description-and-photos'];
    }

    if (objectType === 'garage') {
      // Коммерческая недвижимость: офис, торговая площадь, гараж
      return ['about-object', 'about-building', 'infrastructure', 'description-and-photos', 'price-and-deal-terms'];
    }

    // категории коммерческой с возможностью податься как соб коммерческой
    if (objectType === 'commercialLand') {
      return ['about-object', 'commercial-ownership-verification', 'description-and-photos', 'price-and-deal-terms'];
    }

    if (['office', 'warehouse', 'industry', 'shoppingArea'].includes(objectType)) {
      // Коммерческая недвижимость: офис, торговая площадь
      return [
        'about-object',
        'about-building',
        'infrastructure',
        'commercial-ownership-verification',
        'description-and-photos',
        'price-and-deal-terms',
      ];
    }

    if (objectType === 'business') {
      // Готовый бизнес
      return ['about-object', 'description-and-photos', 'price-and-deal-terms'];
    }

    if (objectType === 'building') {
      // Здание, склад, производство
      return ['about-building', 'commercial-ownership-verification', 'description-and-photos', 'price-and-deal-terms'];
    }

    if (objectType === 'freeAppointmentObject') {
      return [
        'about-object',
        'about-building',
        'commercial-ownership-verification',
        'description-and-photos',
        'price-and-deal-terms',
      ];
    }

    // Основной порядок блоков
    return compact([
      'about-object',
      'about-building',
      'infrastructure',
      'homeowner',
      'description-and-photos',
      'price-and-deal-terms',
    ]);
  };

  // Получить список сегментов в зависимости от авторизации, категории, режима форма (короткая или полная) и др.
  const getSegments = params => {
    const {
      objectType,
      rentDuration,
      ignoreShortForm = false,
      showIndexingDelayAlert = false,
      showRentHelperTrap = false,
      showHomeownerWarning = false,
      isCommercialBusinessAvailable,
      rosreestrData,
    } = params;

    const isShortForm = ignoreShortForm ? false : addFormLayoutService.isShortForm;

    const state = store.getState();

    const segments = [];

    if (!userService.isAuthenticated && !authService.isLastStepLogin) {
      segments.push('auth');
    } else {
      const isPublishedOffer = selectIsPublishedOffer(state);
      const isHomeowner = selectIsHomeowner(state);
      const isByHomeowner = selectIsByHomeowner(state);
      const canPublishAsHomeowner = selectCanPublishAsHomeowner(state);
      const isHomeownersCountPublishedOnly = selectConfigValue('homeownersCountPublishedOnly', state);

      // Показываем для всех, кроме собственников, которым более не доступны публикации от собственника
      // Или это существующее объявление
      const isAddformEnabled = isHomeownersCountPublishedOnly // Только при включенном feature-toggle
        ? !isHomeowner || isPublishedOffer || !isByHomeowner || canPublishAsHomeowner
        : true;

      segments.push('ad-type');

      if (showRentHelperTrap) {
        segments.push('rent-helper');
      }
      if (objectType && !showRentHelperTrap && isCommercialBusinessAvailable) {
        segments.push('address');

        if (!addFormService.needHideRestForm()) {
          if (addFormLayoutService.isShortLayout) {
            segments.push('short-form-switcher');
          }

          if (jkService.showJkForm()) {
            segments.push('about-building', 'ad-submit-jk');
          } else if (isShortForm && !addFormLayoutService.isShortFormAvailable()) {
            segments.push('new-building-form');
          } else {
            const { coworkingOfferType, isByHomeowner } = addFormService.ad;

            Array.prototype.push.apply(
              segments,
              getSegmentsByCategory({
                objectType,
                rentDuration,
                coworkingOfferType,
                rosreestrData,
              }),
            );

            if (isShortForm) {
              removeFromArray(segments, 'about-building');
            }

            if (!isByHomeowner) {
              removeFromArray(segments, 'homeowner');
            }

            if (!commercialOwnerService.isCommercialOwnerWidgetVisible(objectType)) {
              removeFromArray(segments, 'commercial-ownership-verification');
            }

            if (!userService.isAuthenticated && authService.isLastStepLogin) {
              segments.push('auth-last-step');
            } else {
              if (userService.verifiedPhoneNumberInCurrentSession) {
                segments.push('phone-verify', 'contact-info');
              } else if (!userService.user.hasVerifiedPhoneNumber) {
                segments.push('phone-verify');
              } else {
                segments.push('contact-info');
              }

              if (rentHelperService.isFormsAvailable()) {
                segments.push('ad-communication-type');
              }

              segments.push('ad-placing-terms');

              if (showIndexingDelayAlert) {
                segments.push('indexing-delay-alert');
              }

              if (showHomeownerWarning) {
                segments.push('homeowner-warning');
              }

              if (isAddformEnabled) {
                if (publishTermsService.isNewPublishTermsEnabled()) {
                  segments.push('summary');
                } else {
                  segments.push('ad-submit-buttons');
                }
              } else {
                segments.push('disabled-warning');
              }
            }
          }
        }
      }
    }

    return segments;
  };

  return {
    setSegmentVisible,
    isSegmentHidden,
    getSegments,
  };
}
